"use client";

import DownloadIcon from "@mui/icons-material/Download";
import { CircularProgress, Menu } from "@mui/material";
import { useState } from "react";

import useToast from "@/contexts/toast-context";
import { base64ToBlobV2 } from "@/helpers/file-helpers";
import { T2PayHomeMessages } from "@/messages/platforms/talk2PayHome/t2pay-home.messages";
import NotificationItem from "@/mui/Items/NotificationItem";
import t2payService from "@/services/t2pay.service";

import { buildStatementCsv, buildStatementCsvFileName } from "./build-statement-csv";

type ExportFormat = "pdf" | "csv";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: T2PayHomeMessages;
  financialTransactionStartDate: Date;
  financialTransactionEndDate: Date;
}

export function ExportStatementPopover({
  isOpen,
  anchorEl,
  onClose,
  messages,
  financialTransactionStartDate,
  financialTransactionEndDate,
}: Props) {
  const [loadingFormat, setLoadingFormat] = useState<ExportFormat | null>(null);
  const loading = loadingFormat !== null;
  const { showToast } = useToast();

  const triggerFileDownload = (blob: Blob, fileName: string) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = blobUrl;
    link.download = fileName;
    link.target = "_blank";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000); // Cleanup
  };

  const handleDownloadPdf = async (startDate: Date, endDate: Date) => {
    try {
      setLoadingFormat("pdf");
      const response = await t2payService.getFinancialTransactionBase64({ startDate, endDate });

      if ("noContent" in response && response.noContent) {
        showToast({
          duration: 3000,
          text: messages.noTransactionsInPeriod,
          type: "warning",
        });
        return;
      }

      const data = response as { base64: string; name: string };
      if (!data?.base64) {
        throw new Error("Invalid file data");
      }

      const blob = base64ToBlobV2(data.base64, "application/pdf");
      triggerFileDownload(blob, `${data.name}.pdf`);
    } catch (error) {
      showToast({ duration: 10000, text: messages.downloadPdfError, type: "error" });
    } finally {
      setLoadingFormat(null);
    }
  };

  const handleDownloadCsv = async (startDate: Date, endDate: Date) => {
    try {
      setLoadingFormat("csv");
      const financialTransaction = await t2payService.getFinancialTransaction({
        startDate,
        endDate,
      });

      if (!financialTransaction?.transactions?.length) {
        showToast({
          duration: 3000,
          text: messages.noTransactionsInPeriod,
          type: "warning",
        });
        return;
      }

      const csv = buildStatementCsv(financialTransaction);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      triggerFileDownload(blob, buildStatementCsvFileName(startDate, endDate));
    } catch (error) {
      showToast({ duration: 10000, text: messages.downloadCsvError, type: "error" });
    } finally {
      setLoadingFormat(null);
    }
  };

  const renderIcon = (format: ExportFormat) =>
    loadingFormat === format ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />;

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <NotificationItem
        icon={renderIcon("pdf")}
        disabled={loading}
        onClick={() => {
          handleDownloadPdf(financialTransactionStartDate, financialTransactionEndDate);
        }}
        title={messages.pdf}
      />

      <NotificationItem
        icon={renderIcon("csv")}
        disabled={loading}
        onClick={() => {
          handleDownloadCsv(financialTransactionStartDate, financialTransactionEndDate);
        }}
        title={messages.csv}
      />
    </Menu>
  );
}
